
import {
  computed,
  defineComponent, onBeforeMount, PropType, ref, SetupContext, toRaw, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { List } from '@/composables/GraphQL';
import { OperatorType, ShopsLists } from '@/types/generated-types/graphql';
import { UserState } from '@/composables/User';
import SegmentationsPanel from '@/components/segmentations/SegmentationsPanel.vue';
import MultiSelect from 'primevue/multiselect';
import RadioButton from 'primevue/radiobutton';
import Message from 'primevue/message';
import Chip from 'primevue/chip';
import { PanelAccordion } from '@/components/flows/operators/shared/layout';
import type { SpmPanelAccordionInterface } from '@/components/flows/operators/shared/layout/PanelAccordion.vue';
import { ButtonGroup } from '@/components/flows/operators/shared';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import ListCreatePanel from '@/components/mes-lists/ListCreatePanel.vue';
import { myListsState } from '@/composables/mes-listes/ListsManage';
import { asInt } from '@/helpers';
import type { AudienceSetupData } from './index';
import { createDefaultData } from './index';

interface ListOption {
  label: string;
  value: number;
}

interface ListGroup {
  label: string;
  code: string;
  items: ListOption[];
}

function normalizeAllContactsFlag(v: unknown): boolean {
  return v === '1' || v === 1 || v === true;
}

export default defineComponent({
  name: 'TriggerAudienceSetupForm',
  components: {
    ListCreatePanel,
    ButtonGroup,
    FieldErrors,
    SegmentationsPanel,
    MultiSelect,
    RadioButton,
    PanelAccordion,
    Message,
    Chip,
  },

  props: {
    modelValue: {
      type: Object as PropType<AudienceSetupData>,
      required: true,
      default() {
        return createDefaultData();
      },
    },

    formValidation: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
  },

  emits: ['update:modelValue'],

  setup(props: { modelValue: AudienceSetupData; formValidation: any }, context: SetupContext) {
    const { t } = useI18n();
    const idShop = ref(UserState.activeShop ? UserState.activeShop.id : 0);
    const rawSegmentTheList = props.modelValue.segment_the_list as string | number | undefined;
    const segmentTheListData = ref(
      rawSegmentTheList === '1' || Number(rawSegmentTheList) === 1 ? '1' : String(rawSegmentTheList ?? '0'),
    );
    const mountedComponent = ref(false);
    const panelsKeys = ref(0);

    const panels: SpmPanelAccordionInterface[] = [
      {
        id: 'choice_list',
        title: t('automatedScenarios.declencheur.choose_bulk_audience.panel_lists_title'),
      },
      {
        id: 'setup_segmentation',
        title: t('automatedScenarios.declencheur.choose_bulk_audience.panel_segmentation_title'),
      },
    ];

    const formValidationErrors = ref(props.formValidation);
    const componentFieldErrorsKey = ref(0);

    watch(() => props.formValidation, () => {
      formValidationErrors.value = props.formValidation;
      componentFieldErrorsKey.value += 1;
    });

    const segmentTheListOptions: Array<{ value: string; label: string }> = [
      { value: '0', label: t('no') },
      { value: '1', label: t('yes') },
    ];

    const listsOptions = ref<ListGroup[]>([
      {
        label: t('automatedScenarios.declencheur.choose_bulk_audience.lists_group'),
        code: 'lists',
        items: [],
      },
      {
        label: t('automatedScenarios.declencheur.choose_bulk_audience.segments_group'),
        code: 'segments',
        items: [],
      },
    ]);

    const lists = ref<ListOption[]>([]);
    // Initialize audienceMode from modelValue synchronously so the radio cards
    // never flash the default state before onBeforeMount's async work completes.
    const audienceMode = ref<'all_contacts' | 'lists'>(
      normalizeAllContactsFlag(props.modelValue.all_contacts) ? 'all_contacts' : 'lists',
    );
    const segmentsConfiguration = ref<any>(null);

    const isAllContactsMode = computed(() => audienceMode.value === 'all_contacts');

    const showSegmentationsPanel = computed(
      () => segmentTheListData.value === '1' && segmentsConfiguration.value !== null,
    );

    const optionGroupIconClass = (code: string) => {
      if (code === 'lists') return 'far fa-list mr-2';
      return 'far fa-filter mr-2';
    };

    const removeList = (listToRemove: ListOption) => {
      lists.value = lists.value.filter((l) => l.value !== listToRemove.value);
    };

    const update = () => {
      if (mountedComponent.value) {
        const allContacts = isAllContactsMode.value;
        context.emit('update:modelValue', createDefaultData({
          lists: allContacts ? [] : lists.value.map((item) => item.value),
          all_contacts: allContacts ? '1' : '0',
          segment_the_list: segmentTheListData.value,
          segmentation_config: segmentsConfiguration.value,
        }));
      }
    };

    const fetchListsByType = async (type: string): Promise<ListOption[]> => {
      const { items, err } = await List<ShopsLists>({
        name: 'ShopsLists',
        settings: {
          filter: [
            { field: 'id_shop', operator: OperatorType.Equals, value: idShop.value },
            { field: 'active', operator: OperatorType.Equals, value: 1 },
            { field: 'type', operator: OperatorType.Equals, value: type },
            { field: 'valid', operator: OperatorType.Equals, value: 1 },
          ],
          order: [{ field: 'id_shop_list', type: 'ASC' }],
          limit: 0,
          offset: 0,
        },
        fields: ['id_shop_list', 'name'],
      });

      if (err === '') {
        return items.map((item) => ({
          label: item.name,
          value: item.id_shop_list,
        }));
      }

      return [];
    };

    const initListsOptions = async () => {
      listsOptions.value[0].items = await fetchListsByType('list');
      listsOptions.value[1].items = await fetchListsByType('segment');
    };

    // Switching to "all contacts" mode clears any previously selected lists,
    // but keeps the segmentation choice intact (segmentation is now optional
    // in both modes — the user is free to keep or change their answer).
    watch(audienceMode, (mode) => {
      if (!mountedComponent.value) return;
      if (mode === 'all_contacts' && lists.value.length > 0) {
        lists.value = [];
      }
    });

    // When switching to "Yes", ensure config exists or SegmentationsPanel never mounts (empty block).
    watch(segmentTheListData, (v) => {
      if (!mountedComponent.value) return;
      if (v === '1' && segmentsConfiguration.value == null) {
        segmentsConfiguration.value = [];
      }
    });

    watch(
      [lists, segmentTheListData, segmentsConfiguration, audienceMode],
      update,
      { deep: true },
    );

    watch(() => myListsState.refreshComponent, async () => {
      if (myListsState.refreshComponent !== 0) {
        const newListId = myListsState.refreshComponent;
        await initListsOptions();
        myListsState.refreshComponent = 0;

        if (isAllContactsMode.value) {
          return;
        }

        let matchingLists = listsOptions.value[0].items.filter(
          (item) => item.value === asInt(newListId),
        );
        if (!matchingLists.length) {
          matchingLists = listsOptions.value[1].items.filter(
            (item) => item.value === asInt(newListId),
          );
        }

        if (matchingLists.length) {
          const { label, value } = matchingLists[0];
          lists.value.push({ label, value });
        }
        update();
      }
    });

    onBeforeMount(async () => {
      await initListsOptions();

      if (props.modelValue.segmentation_config) {
        segmentsConfiguration.value = toRaw(props.modelValue.segmentation_config);
      } else if (
        props.modelValue.segment_the_list === '1'
        || Number(props.modelValue.segment_the_list) === 1
      ) {
        segmentsConfiguration.value = [];
      }

      // audienceMode is already initialized from props.modelValue at ref
      // creation time (see above), no need to re-assign here.
      if (audienceMode.value === 'lists') {
        props.modelValue.lists.forEach((listId) => {
          let matchingLists = listsOptions.value[0].items.filter((item) => item.value === listId);
          if (!matchingLists.length) {
            matchingLists = listsOptions.value[1].items.filter((item) => item.value === listId);
          }
          if (matchingLists.length) {
            const { label, value } = matchingLists[0];
            lists.value.push({ label, value });
          }
        });
      }

      mountedComponent.value = true;
      update();
    });

    return {
      t,
      panelsKeys,
      panels,
      lists,
      listsOptions,
      audienceMode,
      segmentsConfiguration,
      componentFieldErrorsKey,
      formValidationErrors,
      segmentTheListData,
      segmentTheListOptions,
      mountedComponent,
      removeList,
      isAllContactsMode,
      showSegmentationsPanel,
      optionGroupIconClass,
    };
  },
});
